import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { Link, Redirect } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DataTable } from "@/components/data-table/DataTable";
import type { ColumnDef } from "@/components/data-table/types";
import { useToast } from "@/hooks/use-toast";
import { Copy, Plus, Trash2, UserCheck, UserX, AlertTriangle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { CountryCombobox } from "@/components/ui/country-combobox";
import { getTranslatedNaceSectorOptions } from "@/constants/naceSectors";
import { formatSectorLabel } from "@/lib/sector-labels";

type WorkspaceApi = {
  id: string;
  companyName: string;
  sector: string | null;
  country: string | null;
  operatingCurrency: string;
  reportingPeriodStart: string;
  reportingPeriodEnd: string;
  createdAt: string;
  status?: "active" | "inactive";
  memberCount?: number;
  activeMemberCount?: number;
};

type AdminWorkspaceListResponse = {
  data: WorkspaceApi[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

type MyWorkspaceResponse = {
  workspace: WorkspaceApi;
  membershipRole: "workspace_admin" | "member";
};

type WorkspaceMember = {
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    isActive: boolean;
    companyName: string | null;
  };
  role: "workspace_admin" | "member";
  createdAt: string | null;
};

async function fetchMyWorkspace(fallbackMessage: string): Promise<MyWorkspaceResponse> {
  const res = await fetch("/api/workspaces/me", { credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

async function fetchMyWorkspaceMembers(fallbackMessage: string): Promise<{ workspaceId: string; members: WorkspaceMember[] }> {
  const res = await fetch("/api/workspaces/me/members", { credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

async function updateMyWorkspace(payload: Partial<WorkspaceApi>, fallbackMessage: string) {
  const res = await fetch("/api/workspaces/me", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

async function createMemberInvite(role: "member" | "workspace_admin", fallbackMessage: string) {
  const res = await fetch("/api/workspaces/me/invitations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json() as Promise<{ invitation: { invitationToken: string; role: string; companyName: string; expiresAt: string } }>;
}

async function updateMemberRole(userId: string, role: "member" | "workspace_admin", fallbackMessage: string) {
  const res = await fetch(`/api/workspaces/me/members/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

async function removeMember(userId: string, fallbackMessage: string) {
  const res = await fetch(`/api/workspaces/me/members/${userId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

async function toggleActive(userId: string, fallbackMessage: string) {
  const res = await fetch(`/api/workspaces/me/users/${userId}/toggle-active`, {
    method: "PATCH",
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

async function fetchAdminWorkspaces(params: {
  page: number;
  pageSize: number;
  search?: string;
  sector?: string;
  country?: string;
  status?: "active" | "inactive" | "all";
  sortBy?: "companyName" | "sector" | "country" | "createdAt";
  sortOrder?: "asc" | "desc";
}, fallbackMessage: string): Promise<AdminWorkspaceListResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("page", String(params.page));
  searchParams.set("pageSize", String(params.pageSize));
  if (params.search) searchParams.set("search", params.search);
  if (params.sector && params.sector !== "all") searchParams.set("sector", params.sector);
  if (params.country) searchParams.set("country", params.country);
  if (params.status && params.status !== "all") searchParams.set("status", params.status);
  if (params.sortBy) searchParams.set("sortBy", params.sortBy);
  if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);

  const res = await fetch(`/api/admin/workspaces?${searchParams.toString()}`, { credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

async function fetchAdminWorkspace(id: string, fallbackMessage: string): Promise<{ workspace: WorkspaceApi; members: WorkspaceMember[] }> {
  const res = await fetch(`/api/admin/workspaces/${id}`, { credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

async function deleteAdminWorkspace(id: string, fallbackMessage: string) {
  const res = await fetch(`/api/admin/workspaces/${id}`, { method: "DELETE", credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || fallbackMessage);
  }
  return res.json();
}

function isoToDateInput(value: string | null | undefined) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function AiQuotaCard() {
  const { data, isLoading } = useQuery<{ quota: { budgetCents: number; warningThreshold: number } | null; usedCents: number }>({
    queryKey: ["workspace-ai-quota"],
    queryFn: async () => {
      const res = await fetch("/api/workspace/ai-quota", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  if (isLoading) return null;

  const budgetCents = data?.quota?.budgetCents ?? 0;
  const usedCents = data?.usedCents ?? 0;
  const percent = budgetCents > 0 ? Math.round((usedCents / budgetCents) * 100) : 0;
  const remainingCents = Math.max(0, budgetCents - usedCents);

  if (budgetCents === 0) return null;

  return (
    <div className="bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
      <div className="p-6 border-b border-border/30">
        <h3 className="text-xl font-bold text-foreground">{"AI Usage"}</h3>
        <p className="text-sm text-muted-foreground">{"Contact your platform administrator to adjust your quota."}</p>
      </div>
      <div className="p-6 space-y-4">
        {percent >= (data?.quota?.warningThreshold ?? 80) && (
          <div className="text-sm text-yellow-700 bg-yellow-50 rounded-2xl p-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {`You have used ${percent}% of your AI quota.`}
          </div>
        )}
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>{`Used: $${(usedCents / 100).toFixed(4)}`}</span>
          <span>{`Budget: $${(budgetCents / 100).toFixed(2)}`}</span>
        </div>
        <Progress
          value={Math.min(percent, 100)}
          className={percent > 80 ? "[&>div]:bg-destructive" : ""}
        />
        <p className="text-xs text-muted-foreground">
          {remainingCents > 0
            ? `$${(remainingCents / 100).toFixed(2)} remaining`
            : "Quota exhausted — contact your administrator."}
        </p>
      </div>
    </div>
  );
}

function SettingsCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-3xl shadow-sm outline outline-1 outline-offset-[-1px] outline-border overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

function CardSection({ title, description }: { title: string; description: string }) {
  return (
    <div className="p-6 border-b border-border/30">
      <h3 className="text-xl font-bold text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function MemberAvatar({ firstName, lastName, email }: { firstName: string | null; lastName: string | null; email: string }) {
  const initials = firstName && lastName
    ? `${firstName[0]}${lastName[0]}`.toUpperCase()
    : email.slice(0, 2).toUpperCase();
  return (
    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center shrink-0">
      <span className="text-sm font-bold text-primary">{initials}</span>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const naceOptions = getTranslatedNaceSectorOptions();

  if (user?.role === "contributor") {
    return <Redirect to="/dashboard" />;
  }

  const isPlatformAdmin = user?.role === "admin";

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteRole, setInviteRole] = useState<"member" | "workspace_admin">("member");
  const [createdInviteToken, setCreatedInviteToken] = useState<string | null>(null);

  const [adminFilters, setAdminFilters] = useState({
    page: 1,
    pageSize: 10,
    search: "",
    sector: "all" as string,
    country: "",
    status: "all" as "active" | "inactive" | "all",
    sortBy: "createdAt" as "companyName" | "sector" | "country" | "createdAt",
    sortOrder: "desc" as "asc" | "desc",
  });
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);

  useEffect(() => {
    setAdminFilters((prev) => ({ ...prev, search: debouncedSearch, page: 1 }));
  }, [debouncedSearch]);

  const [deleteWorkspaceId, setDeleteWorkspaceId] = useState<string | null>(null);

  const myWorkspaceQuery = useQuery({
    queryKey: ["/api/workspaces/me"],
    queryFn: () => fetchMyWorkspace("Failed to load workspace"),
    enabled: !!user && !isPlatformAdmin,
  });

  const myMembersQuery = useQuery({
    queryKey: ["/api/workspaces/me/members"],
    queryFn: () => fetchMyWorkspaceMembers("Failed to load members"),
    enabled: !!user && !isPlatformAdmin,
  });

  const adminWorkspacesQuery = useQuery({
    queryKey: ["/api/admin/workspaces", adminFilters],
    queryFn: () => fetchAdminWorkspaces(adminFilters, "Failed to list workspaces"),
    enabled: !!user && isPlatformAdmin,
    placeholderData: (prev) => prev,
  });

  const [workspaceEdit, setWorkspaceEdit] = useState<{
    sector: WorkspaceApi["sector"];
    country: string;
    operatingCurrency: string;
    reportingPeriodStart: string;
    reportingPeriodEnd: string;
  } | null>(null);

  const membershipRole = myWorkspaceQuery.data?.membershipRole;
  const canManageWorkspace = membershipRole === "workspace_admin";

  const workspace = myWorkspaceQuery.data?.workspace;

  useEffect(() => {
    if (!workspace) return;
    setWorkspaceEdit((prev) =>
      prev ?? {
        sector: workspace.sector,
        country: workspace.country ?? "",
        operatingCurrency: workspace.operatingCurrency ?? "USD",
        reportingPeriodStart: isoToDateInput(workspace.reportingPeriodStart),
        reportingPeriodEnd: isoToDateInput(workspace.reportingPeriodEnd),
      }
    );
  }, [workspace]);

  const updateWorkspaceMutation = useMutation({
    mutationFn: (payload: Partial<WorkspaceApi>) => updateMyWorkspace(payload, "Failed to update workspace"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/workspaces/me"] });
      toast({ title: "Workspace updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const createInviteMutation = useMutation({
    mutationFn: () => createMemberInvite(inviteRole, "Failed to create invitation"),
    onSuccess: (data) => {
      setCreatedInviteToken(data.invitation.invitationToken);
      toast({ title: "Invitation created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: "member" | "workspace_admin" }) =>
      updateMemberRole(userId, role, "Failed to update member"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/workspaces/me/members"] });
      toast({ title: "Member updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => removeMember(userId, "Failed to remove member"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/workspaces/me/members"] });
      toast({ title: "Member removed" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (userId: string) => toggleActive(userId, "Failed to toggle user"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/workspaces/me/members"] });
      toast({ title: "User updated" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const inviteLink = createdInviteToken ? `${window.location.origin}/onboarding?token=${createdInviteToken}` : null;

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null);
  const adminWorkspaceQuery = useQuery({
    queryKey: ["/api/admin/workspaces", selectedWorkspaceId],
    queryFn: () => fetchAdminWorkspace(selectedWorkspaceId!, "Failed to load workspace"),
    enabled: !!selectedWorkspaceId && isPlatformAdmin,
  });

  const deleteWorkspaceMutation = useMutation({
    mutationFn: (id: string) => deleteAdminWorkspace(id, "Failed to delete workspace"),
    onSuccess: async (_data, workspaceId) => {
      setDeleteWorkspaceId(null);
      if (selectedWorkspaceId === workspaceId) setSelectedWorkspaceId(null);
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/workspaces"] });
      toast({ title: "Workspace deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  if (!user) return null;

  if (isPlatformAdmin) {
    const adminList = adminWorkspacesQuery.data;
    const totalPages = adminList?.totalPages ?? 1;

    const handleSort = (column: "companyName" | "sector" | "country" | "createdAt") => {
      setAdminFilters((prev) => ({
        ...prev,
        sortBy: column,
        sortOrder: prev.sortBy === column && prev.sortOrder === "asc" ? "desc" : "asc",
        page: 1,
      }));
    };

    const sortIndicator = (column: "companyName" | "sector" | "country" | "createdAt") => {
      if (adminFilters.sortBy !== column) return "";
      return adminFilters.sortOrder === "asc" ? "^" : "v";
    };

    const handlePageChange = (page: number) => {
      setAdminFilters((prev) => ({ ...prev, page: Math.min(Math.max(page, 1), totalPages) }));
    };

    const resetFilters = () => {
      setSearchInput("");
      setAdminFilters((prev) => ({
        ...prev,
        page: 1,
        search: "",
        sector: "all",
        country: "",
        status: "all",
      }));
    };

    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle>{"Admin Settings"}</CardTitle>
            <CardDescription>{"Review, filter, and manage workspaces."}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-5">
              <div className="md:col-span-2 space-y-1.5">
                <Label>{"Search"}</Label>
                <Input
                  placeholder={"Search company"}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{"Sector"}</Label>
                <Select
                  value={adminFilters.sector}
                  onValueChange={(v) => setAdminFilters((prev) => ({ ...prev, sector: (v as any) || "all", page: 1 }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"All"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{"All"}</SelectItem>
                    {naceOptions.map((option) => (
                      <SelectItem key={option.code} value={option.code}>
                        {option.code} - {option.translatedLabel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{"Country"}</Label>
                <Input
                  placeholder={"Country"}
                  value={adminFilters.country}
                  onChange={(e) => setAdminFilters((prev) => ({ ...prev, country: e.target.value, page: 1 }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{"Status"}</Label>
                <Select
                  value={adminFilters.status}
                  onValueChange={(v) => setAdminFilters((prev) => ({ ...prev, status: (v as any) || "all", page: 1 }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"All"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{"All"}</SelectItem>
                    <SelectItem value="active">{"active"}</SelectItem>
                    <SelectItem value="inactive">{"inactive"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-muted-foreground">
                {`Showing page ${adminFilters.page} of ${totalPages} | ${adminList?.total ?? 0} total`}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{"Page size"}</span>
                  <Select
                    value={String(adminFilters.pageSize)}
                    onValueChange={(v) =>
                      setAdminFilters((prev) => ({ ...prev, pageSize: Number(v), page: 1 }))
                    }
                  >
                    <SelectTrigger className="w-[90px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  {"Clear filters"}
                </Button>
              </div>
            </div>

            {adminWorkspacesQuery.error ? (
              <div className="text-sm text-destructive">{(adminWorkspacesQuery.error as Error).message}</div>
            ) : (
              <DataTable
                columns={(
                  [
                    {
                      id: "companyName",
                      header: (
                        <button className="flex items-center gap-1" onClick={() => handleSort("companyName")}>
                          {"Company"} {sortIndicator("companyName")}
                        </button>
                      ),
                      cell: (ws) => <span className="font-medium">{ws.companyName}</span>,
                    },
                    {
                      id: "sector",
                      header: (
                        <button className="flex items-center gap-1" onClick={() => handleSort("sector")}>
                          {"Sector"} {sortIndicator("sector")}
                        </button>
                      ),
                      cell: (ws) => (
                        <span className="text-muted-foreground">{ws.sector ? formatSectorLabel(ws.sector) : "-"}</span>
                      ),
                    },
                    {
                      id: "country",
                      header: (
                        <button className="flex items-center gap-1" onClick={() => handleSort("country")}>
                          {"Country"} {sortIndicator("country")}
                        </button>
                      ),
                      cell: (ws) => <span className="text-muted-foreground">{ws.country ?? "-"}</span>,
                    },
                    {
                      id: "created",
                      header: (
                        <button className="flex items-center gap-1" onClick={() => handleSort("createdAt")}>
                          {"Created"} {sortIndicator("createdAt")}
                        </button>
                      ),
                      cell: (ws) => {
                        const createdDate = ws.createdAt ? new Date(ws.createdAt) : null;
                        const createdLabel =
                          createdDate && !Number.isNaN(createdDate.getTime()) ? createdDate.toLocaleDateString() : "-";
                        return <span className="text-muted-foreground">{createdLabel}</span>;
                      },
                    },
                    {
                      id: "status",
                      header: "Status",
                      cell: (ws) => (
                        <Badge variant={ws.status === "active" ? "secondary" : "destructive"}>
                          {ws.status === "active" ? "active" : "inactive"}
                        </Badge>
                      ),
                    },
                    {
                      id: "actions",
                      header: "Actions",
                      align: "right",
                      cell: (ws) => (
                        <div className="flex justify-end gap-2">
                          <Dialog open={selectedWorkspaceId === ws.id} onOpenChange={(open) => setSelectedWorkspaceId(open ? ws.id : null)}>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" onClick={() => setSelectedWorkspaceId(ws.id)}>
                                {"View"}
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl">
                              <DialogHeader>
                                <div className="flex items-start justify-between gap-4">
                                  <div>
                                    <DialogTitle>{ws.companyName}</DialogTitle>
                                    <div className="text-sm text-muted-foreground mt-1">
                                      {ws.sector ? formatSectorLabel(ws.sector) : "No sector"} / {ws.country ?? "No country"}
                                    </div>
                                  </div>
                                  <Button asChild variant="secondary">
                                    <Link href={`/admin/workspaces/${ws.id}/reports`}>{"View reports & data"}</Link>
                                  </Button>
                                </div>
                              </DialogHeader>
                              {adminWorkspaceQuery.isLoading ? (
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                              ) : adminWorkspaceQuery.data ? (
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <div className="text-muted-foreground">{"Sector"}</div>
                                      <div>{formatSectorLabel(adminWorkspaceQuery.data.workspace.sector)}</div>
                                    </div>
                                    <div>
                                      <div className="text-muted-foreground">{"Country"}</div>
                                      <div>{adminWorkspaceQuery.data.workspace.country ?? "-"}</div>
                                    </div>
                                  </div>
                                  <div className="rounded-lg border border-border overflow-hidden">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>{"User"}</TableHead>
                                          <TableHead>{"Role"}</TableHead>
                                          <TableHead>{"Status"}</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {adminWorkspaceQuery.data.members.map((m) => (
                                          <TableRow key={m.user.id}>
                                            <TableCell className="font-medium">
                                              {m.user.firstName && m.user.lastName ? `${m.user.firstName} ${m.user.lastName}` : m.user.email}
                                            </TableCell>
                                            <TableCell>
                                              <Badge variant={m.role === "workspace_admin" ? "default" : "secondary"}>{m.role}</Badge>
                                            </TableCell>
                                            <TableCell>
                                              <Badge variant={m.user.isActive ? "secondary" : "destructive"}>
                                                {m.user.isActive ? "active" : "disabled"}
                                              </Badge>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-destructive">
                                  {(adminWorkspaceQuery.error as Error | null)?.message || "Failed to load workspace"}
                                </div>
                              )}
                            </DialogContent>
                          </Dialog>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => setDeleteWorkspaceId(ws.id)}
                            disabled={deleteWorkspaceMutation.isPending}
                          >
                            {"Delete"}
                          </Button>
                        </div>
                      ),
                    },
                  ] as ColumnDef<WorkspaceApi>[]
                )}
                data={adminList?.data ?? []}
                getRowId={(ws) => ws.id}
                isLoading={adminWorkspacesQuery.isLoading}
                emptyState={{ title: "No workspaces found" }}
                pagination={
                  adminList
                    ? {
                        page: adminFilters.page,
                        pageSize: adminFilters.pageSize,
                        total: adminList.total,
                        totalPages,
                        onPageChange: handlePageChange,
                      }
                    : undefined
                }
              />
            )}
          </CardContent>
        </Card>
        <Dialog open={!!deleteWorkspaceId} onOpenChange={(open) => setDeleteWorkspaceId(open ? deleteWorkspaceId : null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{"Delete workspace?"}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                {"This is a hard delete and will remove the workspace and all related data. This action cannot be undone."}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteWorkspaceId(null)} disabled={deleteWorkspaceMutation.isPending}>
                  {"Cancel"}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deleteWorkspaceId && deleteWorkspaceMutation.mutate(deleteWorkspaceId)}
                  disabled={deleteWorkspaceMutation.isPending || !deleteWorkspaceId}
                >
                  {deleteWorkspaceMutation.isPending ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-4xl font-black tracking-tight text-primary">
          {"Workspace Settings"}
        </h1>
        <p className="text-lg text-muted-foreground">
          {"Manage your workspace information."}
        </p>
      </div>

      {/* Workspace Information Card */}
      <SettingsCard>
        <CardSection
          title={"Company Name" ? "Workspace Information" : "Workspace Information"}
          description={"Manage your workspace information."}
        />
        {myWorkspaceQuery.isLoading ? (
          <div className="p-6 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : myWorkspaceQuery.error ? (
          <div className="p-6 text-sm text-destructive">{(myWorkspaceQuery.error as Error).message}</div>
        ) : workspace && workspaceEdit ? (
          <>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="font-semibold">{"Company Name"}</Label>
                  <Input value={workspace.companyName} disabled className="rounded-2xl bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">{"Operating Currency"}</Label>
                  <Input
                    value={workspaceEdit.operatingCurrency}
                    onChange={(e) => setWorkspaceEdit({ ...workspaceEdit, operatingCurrency: e.target.value })}
                    disabled={!canManageWorkspace}
                    className="rounded-2xl bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">{"Country"}</Label>
                  <CountryCombobox
                    value={workspaceEdit.country}
                    onValueChange={(v) => setWorkspaceEdit({ ...workspaceEdit, country: v })}
                    disabled={!canManageWorkspace}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">{"Sector"}</Label>
                  <Select
                    value={workspaceEdit.sector ?? ""}
                    onValueChange={(v) => setWorkspaceEdit({ ...workspaceEdit, sector: (v || null) as any })}
                    disabled={!canManageWorkspace}
                  >
                    <SelectTrigger className="rounded-2xl bg-muted">
                    <SelectValue placeholder={"Select sector"} />
                    </SelectTrigger>
                    <SelectContent>
                      {naceOptions.map((option) => (
                        <SelectItem key={option.code} value={option.code}>
                          {option.code} - {option.translatedLabel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-muted/30 flex justify-end">
              <Button
                className="rounded-2xl px-6 py-2 font-bold"
                onClick={() => {
                  if (!workspaceEdit) return;
                  updateWorkspaceMutation.mutate({
                    sector: workspaceEdit.sector,
                    country: workspaceEdit.country || null,
                    operatingCurrency: workspaceEdit.operatingCurrency,
                    reportingPeriodStart: workspaceEdit.reportingPeriodStart,
                    reportingPeriodEnd: workspaceEdit.reportingPeriodEnd,
                  } as any);
                }}
                disabled={!canManageWorkspace || updateWorkspaceMutation.isPending}
              >
                {"Save"}
              </Button>
            </div>
          </>
        ) : null}
      </SettingsCard>

      {/* Reporting Period Card */}
      {workspace && workspaceEdit && (
        <SettingsCard>
          <CardSection
            title={"Reporting Period Start".replace(" Start", "") || "Reporting Period"}
            description={"Manage your workspace information."}
          />
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-semibold">{"Reporting Period Start"}</Label>
                <Input
                  type="date"
                  value={workspaceEdit.reportingPeriodStart}
                  onChange={(e) => setWorkspaceEdit({ ...workspaceEdit, reportingPeriodStart: e.target.value })}
                  disabled={!canManageWorkspace}
                  className="rounded-2xl bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">{"Reporting Period End"}</Label>
                <Input
                  type="date"
                  value={workspaceEdit.reportingPeriodEnd}
                  onChange={(e) => setWorkspaceEdit({ ...workspaceEdit, reportingPeriodEnd: e.target.value })}
                  disabled={!canManageWorkspace}
                  className="rounded-2xl bg-muted"
                />
              </div>
            </div>
            <div className="p-4 bg-primary/10 rounded-2xl flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/80">
                These dates define the default filtering range for your performance dashboards and annual sustainability reports. Changing this will recalculate your baseline metrics.
              </p>
            </div>
          </div>
        </SettingsCard>
      )}

      {/* Users Card */}
      <SettingsCard>
        <div className="px-6 py-6 border-b border-border/30 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-foreground">{"Users"}</h3>
            <p className="text-sm text-muted-foreground">{"Manage contributors in your workspace."}</p>
          </div>
          <Dialog open={inviteDialogOpen} onOpenChange={(open) => {
            setInviteDialogOpen(open);
            if (!open) setCreatedInviteToken(null);
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-2xl gap-2 font-bold" disabled={!canManageWorkspace}>
                <Plus className="h-4 w-4" />
                {"Invite User"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{"Create Invitation"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{"Role"}</Label>
                  <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as any)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">{"Member"}</SelectItem>
                      <SelectItem value="workspace_admin">{"Workspace Admin"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={() => createInviteMutation.mutate()} disabled={createInviteMutation.isPending}>
                  {"Create Link"}
                </Button>
                {inviteLink && (
                  <div className="space-y-2">
                    <Label>{"Invite Link"}</Label>
                    <div className="flex gap-2">
                      <Input value={inviteLink} readOnly />
                      <Button
                        variant="outline"
                        onClick={async () => {
                          await navigator.clipboard.writeText(inviteLink);
                          toast({ title: "Copied" });
                        }}
                        title={"Copy invite link"}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div>
          {myMembersQuery.isLoading ? (
            <div className="p-6 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : myMembersQuery.error ? (
            <div className="p-6 text-sm text-destructive">{(myMembersQuery.error as Error).message}</div>
          ) : (
            <>
              {/* Table Header */}
              <div className="bg-muted/30 grid grid-cols-[1fr_10rem_13rem_10rem] px-6 py-3">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{"User"}</span>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{"Role"}</span>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{"Status"}</span>
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right">{"Actions"}</span>
              </div>
              {/* Table Rows */}
              {myMembersQuery.data?.members.map((m) => {
                const displayName = m.user.firstName && m.user.lastName
                  ? `${m.user.firstName} ${m.user.lastName}`
                  : m.user.email;
                return (
                  <div key={m.user.id} className="grid grid-cols-[1fr_10rem_13rem_10rem] px-6 py-4 items-center border-t border-border/10">
                    {/* User with avatar */}
                    <div className="flex items-center gap-3">
                      <MemberAvatar firstName={m.user.firstName} lastName={m.user.lastName} email={m.user.email} />
                      <div>
                        <div className="text-sm font-bold text-foreground">{displayName}</div>
                        {m.user.firstName && m.user.lastName && (
                          <div className="text-xs text-muted-foreground">{m.user.email}</div>
                        )}
                      </div>
                    </div>
                    {/* Role */}
                    <div>
                      {canManageWorkspace ? (
                        <Select
                          value={m.role}
                          onValueChange={(v) => updateMemberMutation.mutate({ userId: m.user.id, role: v as any })}
                        >
                          <SelectTrigger className="h-9 w-[150px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">{"Member"}</SelectItem>
                            <SelectItem value="workspace_admin">{"Workspace Admin"}</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-sm text-foreground">{m.role === "workspace_admin" ? "Admin" : "Member"}</span>
                      )}
                    </div>
                    {/* Status with dot */}
                    <div>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        m.user.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-amber-100 text-amber-800"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${m.user.isActive ? "bg-green-500" : "bg-amber-500"}`} />
                        {m.user.isActive ? "active" : "disabled"}
                      </span>
                    </div>
                    {/* Actions */}
                    <div className="flex justify-end gap-1">
                      {canManageWorkspace ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => toggleActiveMutation.mutate(m.user.id)}
                            disabled={toggleActiveMutation.isPending}
                            title={m.user.isActive ? "Disable account" : "Enable account"}
                          >
                            {m.user.isActive ? (
                              <UserX className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <UserCheck className="h-4 w-4 text-green-600" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => removeMemberMutation.mutate(m.user.id)}
                            disabled={removeMemberMutation.isPending}
                            title={"Remove user"}
                          >
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </>
                      ) : (
                        <span className="text-muted-foreground text-sm">{"-"}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </SettingsCard>

      <AiQuotaCard />
    </div>
  );
}
